import type { BudgetPeriodType } from '@/api/budget'
import { periodBounds, type FyStartMonth } from './budgetPeriod'

const DAY_MS = 86_400_000

function dayNumber(d: Date): number {
  return Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS)
}

/** How far through a budget period `today` is. Days left count today; a past or future period has none. */
export function periodProgress(
  type: BudgetPeriodType,
  period: string,
  fy: FyStartMonth,
  today: Date
): { isCurrent: boolean; elapsedFraction: number; daysLeft: number } {
  const { start, end } = periodBounds(type, period, fy)
  const first = dayNumber(start)
  const last = dayNumber(end)
  const now = dayNumber(today)
  if (now > last) return { isCurrent: false, elapsedFraction: 1, daysLeft: 0 }
  if (now < first) return { isCurrent: false, elapsedFraction: 0, daysLeft: 0 }
  const total = last - first + 1
  return { isCurrent: true, elapsedFraction: (now - first) / total, daysLeft: last - now + 1 }
}

/** What can be spent each remaining day, in whole rupees; null when there's nothing to split. */
export function perDay(remaining: number, daysLeft: number): number | null {
  if (remaining <= 0 || daysLeft <= 0) return null
  return Math.floor(remaining / daysLeft)
}

function shareUsed(row: { spent: number; limit: number }): number {
  if (row.limit === 0) return row.spent > 0 ? Infinity : 0
  return row.spent / row.limit
}

/** Over budget first, then by share used, most first. */
export function sortWorstFirst<T extends { spent: number; limit: number }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const overA = a.spent > a.limit ? 1 : 0
    const overB = b.spent > b.limit ? 1 : 0
    return overB - overA || shareUsed(b) - shareUsed(a)
  })
}
